import api from '@api';
import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ProductListType, ProductType } from 'src/types/product';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { ProductDetailGridTableRefType, ExportTypeType, GridRequestType } from 'src/types/table';
import {
    clearProductDetail,
    createProductDetailFetch,
    deleteProductFetch,
    getProductDetailChangelogFetch,
    getProductDetailFetch,
    getProductDetailOrdersFetch,
    getProductDetailStockFetch,
    getProductDetailStockMovementsFetch,
    getProductDetailProductReceivingsFetch,
    getProductDetailProductOrdersGridFetch,
    getProductDetailProductReturnsFetch,
    updateProductDetailFetch
} from 'src/resources/productDetail/reducer';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import getBackUrl from 'src/utils/getBackUrl';
import { EDIT_KEYS, EditKeysType, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import ProductStockTable from './ProductStockTable';
import RenderComponents from './RenderComponents';
import { stockMovementsExportExtendOptions } from './ProductStockMovementsTable';
import moment from 'moment';
import isDeepEqual from 'src/utils/isDeepEqual';
import { setStorageData } from 'src/resources/storage/reducer';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import { samePaths } from 'src/utils/checkPaths';
import getArrToObjectOfTrues from 'src/utils/getArrToObjectOfTrues';
import { FormErrorsType, OnChangeType } from '../../types/global';
import {
    hasProductSetChanged,
    getPendingOrdersForStatuses,
    formatOrderIdentifier,
    getProductTypeChangeKind,
    PENDING_ORDER_STATUSES,
    mapEansForSave
} from './functions';
import { productApiToEditData } from 'src/utils/productFiles';

const ORDERS_CHECK_TIME_FROM_YEARS = 1;
const initData: ProductType = { FILES: [] };
const ProductDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idProduct } = useParams();

    const newProduct = !idProduct;

    const formRef = useRef<HTMLFormElement>(null);
    const stockMovementsTableRef = useRef<ProductDetailGridTableRefType>(null);
    const productReceivingsTableRef = useRef<ProductDetailGridTableRefType>(null);
    const productOrdersTableRef = useRef<ProductDetailGridTableRefType>(null);
    const productReturnsTableRef = useRef<ProductDetailGridTableRefType>(null);

    const data = useSelector((state: RootStoreType) => state.productDetail.data);
    const changelog = useSelector((state: RootStoreType) => state.productDetail.changelog);
    const dataStock = useSelector((state: RootStoreType) => state.productDetail.dataStock);
    const dataOrders = useSelector((state: RootStoreType) => state.productDetail.dataOrders);
    const dataStockMovements = useSelector((state: RootStoreType) => state.productDetail.dataStockMovements);
    const dataProductReceivings = useSelector((state: RootStoreType) => state.productDetail.dataProductReceivings);
    const dataProductOrdersGrid = useSelector((state: RootStoreType) => state.productDetail.dataProductOrdersGrid);
    const dataProductReturns = useSelector((state: RootStoreType) => state.productDetail.dataProductReturns);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const loading = useSelector((state: RootStoreType) => state.productDetail.loading);
    const apiErrorKeys = useSelector((state: RootStoreType) => state.productDetail.apiErrorKeys);
    const stockRation = useSelector((state: RootStoreType) => state.provider.data?.SHOPTET_STOCK_RATIO);
    const customsWarehouseEnabled = useSelector((state: RootStoreType) => state.provider.data?.CUSTOMS_WAREHOUSE_ENABLED);
    const orderStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderStatusCodes);
    const orderStatusCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.orderStatusCodes);
    const shipperCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperCodes);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const paymentCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.paymentCodes);
    const orderMarketplacesCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.orderMarketplacesCodes
    );
    const productTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productTypeCodes);
    const productTypeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.productTypeCodes);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);
    const scanImeiSnCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.scanImeiSnCodes);
    const scanImeiSnCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.scanImeiSnCodes);
    const batteryTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.batteryTypeCodes);
    const batteryTypeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.batteryTypeCodes);
    const stockMovementTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.stockMovementTypeCodes);
    const activeNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.activeNumberCodes);
    const activeNumberCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.activeNumberCodes
    );
    const productInsetTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.productInsetTypeCodes
    );
    const productInsetTypeCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.productInsetTypeCodes
    );
    const insertByOrderTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.insertByOrderTypeCodes
    );
    const insertByOrderTypeCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.insertByOrderTypeCodes
    );
    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);
    const expeditionMethodCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes
    );
    const expeditionMethodCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.expeditionMethodCodes
    );
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const currencyCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.currencyCodes);
    const productRelationCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productRelationCodes);
    const productRelationCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.productRelationCodes
    );
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const [editData, setEditData] = useState<ProductType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [pendingOrdersCheckRequested, setPendingOrdersCheckRequested] = useState<boolean>(false);
    const [pendingOrdersCheckReason, setPendingOrdersCheckReason] = useState<'typeChange' | 'setComposition' | null>(
        null
    );
    const [showProductStockTable, setShowProductStockTable] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('detail');
    const [relationProductsList, setRelationProductsList] = useState<ProductListType[] | null>(null);

    const disableEdit = newProduct ? !useCheckPermission('PRODUCT.CREATE') : !useCheckPermission('PRODUCT.UPDATE');
    const permissionEditDimensionsWeight = useCheckPermission('PRODUCT.EDIT_DIMENSIONS_WEIGHT');
    const permissionExpMethodEdit = useCheckPermission('PRODUCT.EXP_METHOD.EDIT');
    const permissionEanEdit = useCheckPermission('PRODUCT.EAN.EDIT');
    const permissionGlobalProduct = useCheckPermission('PRODUCT.GLOBAL_PRODUCT');
    const permissionQuantityConversion = useCheckPermission('PRODUCT.QUANTITY_CONVERSION');
    const permissionDelete = useCheckPermission('PRODUCT.DELETE');
    const permissionBillOfMaterials = useCheckPermission('BILL_OF_MATERIALS.LIST');
    const permissionOrderDetail = useCheckPermission('ORDER.DETAIL');
    const permissionReceivingList = useCheckPermission('RECEIVING.LIST');
    const permissionReceivingDetail = useCheckPermission('RECEIVING.DETAIL');
    const permissionReturnList = useCheckPermission('RETURN.LIST');
    const permissionReturnDetail = useCheckPermission('RETURN.DETAIL');
    const receivingStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingStatusCodes);
    const receivingStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.receivingStatusCodes
    );
    const returnStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.returnStatusCodes);
    const returnStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.returnStatusCodes
    );

    const backUrl = getBackUrl(locationHistory, '/products/active/', '/products/new');

    useEffect(() => {
        if (idProduct) {
            if (data?.SKU) {
                dispatch(
                    setLocationParams({
                        name: translate('productDetail.title', { idProduct, SKU: data?.SKU }),
                        backUrl
                    })
                );
            }
        } else {
            dispatch(setLocationParams({ name: translate('productDetail.titleNew'), backUrl }));
        }
    }, [idProduct, backUrl, locationHistory, data?.SKU]);

    useEffect(() => {
        if (editData.INSERT_TYPE === 'specificProduct' && !disableEdit && !relationProductsList) {
            api<ProductListType[]>('get', 'api/secure/products?simple=true').then((res) =>
                setRelationProductsList(res.data)
            );
        }
    }, [editData.INSERT_TYPE, disableEdit, relationProductsList]);

    useEffect(() => {
        if (idProduct) {
            dispatch(getProductDetailFetch(idProduct));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearProductDetail());
        };
    }, [idProduct]);

    useEffect(() => {
        if (data) {
            setEditData(productApiToEditData(data));
        }

        if (!idProduct && data?.ID) {
            navigate('/products/' + String(data.ID));
            setThisStorageData(null);
        }
    }, [data]);

    useEffect(() => {
        setEditErrors(getArrToObjectOfTrues(apiErrorKeys));
    }, [apiErrorKeys]);

    // After requesting orders check for product type / set change, evaluate and show warning or proceed to save
    useEffect(() => {
        if (!pendingOrdersCheckRequested || loading || dataOrders === undefined || !pendingOrdersCheckReason) return;
        setPendingOrdersCheckRequested(false);
        const reason = pendingOrdersCheckReason;
        setPendingOrdersCheckReason(null);

        const pendingOrders = getPendingOrdersForStatuses(dataOrders, PENDING_ORDER_STATUSES);

        if (reason === 'typeChange') {
            if (pendingOrders.length === 0) {
                performSave({ updateOrders: true });
                return;
            }
            const sku = data?.SKU ?? editData.SKU ?? '';
            const originalType =
                productTypeCodesObject[data?.PRODUCT_TYPE ?? '']?.label ?? data?.PRODUCT_TYPE ?? '';
            const newType =
                productTypeCodesObject[editData.PRODUCT_TYPE ?? '']?.label ?? editData.PRODUCT_TYPE ?? '';
            const line1 = translate('productDetail.productTypeChangeOrdersConfirm.line1', {
                originalType,
                newType
            });
            const line2 = translate('productDetail.productTypeChangeOrdersConfirm.line2', {
                sku,
                count: pendingOrders.length
            });
            setConfirmParams({
                title: translate('productDetail.saveChanges'),
                content: `${line1}\n${line2}`,
                okLabel: 'massEditProductDialog.ok',
                action: () => performSave({ updateOrders: true }),
                onCancel: () => {}
            });
            return;
        }

        if (pendingOrders.length === 0) {
            performSave();
            return;
        }
        const sku = data?.SKU ?? '';
        const title = translate('productDetail.productSetChangeWarning.info', { count: pendingOrders.length });
        const text = translate('productDetail.productSetChangeWarning.text', { sku });
        const ordersPrefix = translate('productDetail.productSetChangeWarning.ordersPrefix');
        const ordersLines = pendingOrders.map(formatOrderIdentifier).join('\n');
        const content = `${text}\n\n${ordersPrefix}\n${ordersLines}`;
        setConfirmParams({
            title,
            content,
            action: () => performSave(),
            onCancel: () => {}
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- performSave intentionally not in deps to avoid re-running when it changes
    }, [
        pendingOrdersCheckRequested,
        pendingOrdersCheckReason,
        loading,
        dataOrders,
        data?.SKU,
        data?.PRODUCT_TYPE,
        editData.SKU,
        editData.PRODUCT_TYPE,
        productTypeCodesObject
    ]);

    useEffect(() => {
        if (!changelog && activeTab === 'history') {
            dispatch(getProductDetailChangelogFetch(idProduct));
        }
    }, [activeTab, changelog, idProduct]);

    // save edit data -start
    const editDataRef = useRef<ProductType>({});
    useEffect(() => {
        if (
            newProduct &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/products/active') ||
                samePaths(locationHistory[1]?.pathname, '/products/new'))
        ) {
            setConfirmParams({
                title: translate('productDetail.confirmGetNewDataStorage'),
                content: translate('productDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                }
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: ProductType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeMultiple = (updates: Partial<ProductType>) => {
        const newData = { ...editData, ...updates };
        const newErrors = { ...editErrors };
        // Vymazat chyby pro všechny aktualizované klíče
        Object.keys(updates).forEach((key) => {
            delete newErrors[key];
        });
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleDeleteProduct = () => {
        setConfirmParams({
            title: translate('productDetail.deleteProduct'),
            content: translate('productDetail.deleteProductConfirm'),
            action: () => dispatch(deleteProductFetch(idProduct))
        });
    };

    const performSave = (options?: { updateOrders?: boolean }) => {
        const formatData = { ...editData };
        if (!formatData.PRICE) formatData.PRICE = null;
        if (!formatData.WIDTH) formatData.WIDTH = null;
        if (!formatData.HEIGHT) formatData.HEIGHT = null;
        if (!formatData.LENGTH) formatData.LENGTH = null;
        if (!formatData.WEIGHT) formatData.WEIGHT = null;

        formatData.PRODUCT_RELATIONS_2 = (formatData.PRODUCT_RELATIONS_2 || []).map(
            (productRelation: any): any => {
                const relationData = {
                    ID_PRODUCT_1: productRelation.ID_PRODUCT_1,
                    TYPE: productRelation.TYPE,
                    ID: productRelation.ID
                };
                if (productRelation.ID < 1) {
                    delete relationData.ID;
                }
                return relationData;
            }
        );

        if (formatData.PRODUCT_SET && formatData.PRODUCT_SET.length) {
            formatData.PRODUCT_SET = formatData.PRODUCT_SET.map((item: any) => ({
                PRODUCT_SKU: item.PRODUCT_SKU,
                PRODUCT_COUNT: item.PRODUCT_COUNT
            }));
        }

        if (formatData.EANS) {
            formatData.EANS = mapEansForSave(formatData.EANS);
            // EANS je zdroj pravdy — nenechat odeslat legacy/joined `EAN` ("a,b"),
            // jinak Core upsertne celý řetězec jako jeden řádek product_eans.
            delete formatData.EAN;
        }

        if (newProduct) {
            dispatch(createProductDetailFetch(formatData));
        } else {
            dispatch(updateProductDetailFetch({ idProduct, data: formatData, updateOrders: options?.updateOrders }));
        }
    };

    const openSaveConfirm = () => {
        setConfirmParams({
            title: translate(newProduct ? 'productDetail.create' : 'productDetail.save'),
            content: translate(newProduct ? 'productDetail.createConfirm' : 'productDetail.saveConfirm'),
            action: () => performSave()
        });
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (editData.PRODUCT_TYPE === 'pack' && (!editData.PRODUCT_SET || !editData.PRODUCT_SET.length)) {
            newFormErrors.PRODUCT_SET = true;
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('messages.formErrorTitle'),
                    text: translate('productDetail.productSetTab.noProductsError')
                })
            );
            setEditErrors(newFormErrors);
            return true;
        }

        if (!Object.keys(newFormErrors).length) {
            const typeChangeKind = !newProduct
                ? getProductTypeChangeKind(data?.PRODUCT_TYPE, editData.PRODUCT_TYPE)
                : null;

            if (typeChangeKind) {
                setPendingOrdersCheckReason('typeChange');
                setPendingOrdersCheckRequested(true);
                dispatch(
                    getProductDetailOrdersFetch({
                        idProduct,
                        timeFrom: moment().subtract(ORDERS_CHECK_TIME_FROM_YEARS, 'year')
                    })
                );
                return false;
            }

            const isPackWithSetChange =
                !newProduct &&
                editData.PRODUCT_TYPE === 'pack' &&
                hasProductSetChanged(data?.PRODUCT_SET, editData.PRODUCT_SET);

            if (isPackWithSetChange) {
                setPendingOrdersCheckReason('setComposition');
                setPendingOrdersCheckRequested(true);
                dispatch(
                    getProductDetailOrdersFetch({
                        idProduct,
                        timeFrom: moment().subtract(ORDERS_CHECK_TIME_FROM_YEARS, 'year')
                    })
                );
                return false;
            }

            openSaveConfirm();
            return false;
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
            setEditErrors(newFormErrors);
            return true;
        }
    };

    const handleGetProductDetailStockFetch = () => {
        dispatch(getProductDetailStockFetch(idProduct));
    };
    const handleGetProductDetailStockMovementsFetch = (payload: GridRequestType) => {
        if (idProduct) {
            dispatch(getProductDetailStockMovementsFetch({ idProduct, gridPayload: payload }));
        }
    };

    const handleGetProductDetailProductReceivingsFetch = (payload: GridRequestType) => {
        if (idProduct && permissionReceivingList) {
            dispatch(getProductDetailProductReceivingsFetch({ idProduct, gridPayload: payload }));
        }
    };

    const handleGetProductDetailProductOrdersGridFetch = (payload: GridRequestType) => {
        if (idProduct) {
            dispatch(getProductDetailProductOrdersGridFetch({ idProduct, gridPayload: payload }));
        }
    };

    const handleGetProductDetailProductReturnsFetch = (payload: GridRequestType) => {
        if (idProduct && permissionReturnList) {
            dispatch(getProductDetailProductReturnsFetch({ idProduct, gridPayload: payload }));
        }
    };

    useEffect(() => {
        if (activeTab === 'productReceivings' && !permissionReceivingList) {
            setActiveTab('detail');
        }
    }, [activeTab, permissionReceivingList]);

    useEffect(() => {
        if (activeTab === 'productReturns' && !permissionReturnList) {
            setActiveTab('detail');
        }
    }, [activeTab, permissionReturnList]);

    const handleExportStockMovements = (type: ExportTypeType) => {
        if (stockMovementsTableRef.current && idProduct) {
            stockMovementsTableRef.current.exportTableGrid(
                type,
                translate('productDetail.exportStockMovements'),
                () => stockMovementsTableRef.current!.getGridParams(),
                'api/secure/stock-movements/grid',
                undefined,
                stockMovementsExportExtendOptions
            );
        }
    };

    const handleExportProductReceivings = (type: ExportTypeType) => {
        if (productReceivingsTableRef.current && idProduct) {
            productReceivingsTableRef.current.exportTableGrid(
                type,
                translate('productDetail.exportProductReceivings'),
                () => productReceivingsTableRef.current!.getGridParams(),
                `api/secure/products/${idProduct}/receivings/grid`
            );
        }
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const handleExportProductOrders = (type: ExportTypeType) => {
        if (productOrdersTableRef.current && idProduct) {
            productOrdersTableRef.current.exportTableGrid(
                type,
                translate('productDetail.exportProductOrders'),
                () => productOrdersTableRef.current!.getGridParams(),
                `api/secure/products/${idProduct}/orders/grid`
            );
        }
    };

    const handleExportProductReturns = (type: ExportTypeType) => {
        if (productReturnsTableRef.current && idProduct) {
            productReturnsTableRef.current.exportTableGrid(
                type,
                translate('productDetail.exportProductReturns'),
                () => productReturnsTableRef.current!.getGridParams(),
                `api/secure/products/${idProduct}/returns/grid`
            );
        }
    };

    return (
        <>
            <TopBlock
                data={data}
                idProduct={idProduct}
                newProduct={newProduct}
                disableEdit={disableEdit}
                permissionDelete={permissionDelete}
                permissionBillOfMaterials={permissionBillOfMaterials}
                loading={!!loading}
                handleSave={handleSave}
                handleDeleteProduct={handleDeleteProduct}
                showProductStockTable={showProductStockTable}
                setShowProductStockTable={setShowProductStockTable}
                activeTab={activeTab}
                onChangeTab={(value) => setActiveTab(value)}
                handleExportStockMovements={handleExportStockMovements}
                handleExportProductReceivings={handleExportProductReceivings}
                handleExportProductOrders={handleExportProductOrders}
                handleExportProductReturns={handleExportProductReturns}
                permissionReceivingList={permissionReceivingList}
                permissionReturnList={permissionReturnList}
                productType={editData.PRODUCT_TYPE}
            />

            {loading && <Loading />}

            <RenderComponents
                data={data}
                onChange={onChange}
                onChangeMultiple={onChangeMultiple}
                editData={editData}
                editErrors={editErrors}
                newProduct={newProduct}
                disableEdit={disableEdit}
                activeTab={activeTab}
                changelog={changelog}
                formRef={formRef}
                permissionQuantityConversion={permissionQuantityConversion}
                permissionGlobalProduct={permissionGlobalProduct}
                permissionEditDimensionsWeight={permissionEditDimensionsWeight}
                permissionExpMethodEdit={permissionExpMethodEdit}
                productTypeCodes={productTypeCodes}
                boolNumberCodes={boolNumberCodes}
                scanImeiSnCodes={scanImeiSnCodes}
                activeNumberCodes={activeNumberCodes}
                batteryTypeCodes={batteryTypeCodes}
                productInsetTypeCodes={productInsetTypeCodes}
                expeditionMethodCodes={expeditionMethodCodes}
                currencyCodes={currencyCodes}
                productRelationCodes={productRelationCodes}
                productRelationCodesObject={productRelationCodesObject}
                productTypeCodesObject={productTypeCodesObject}
                batteryTypeCodesObject={batteryTypeCodesObject}
                dataStockMovements={dataStockMovements}
                dataProductReceivings={dataProductReceivings}
                dataProductOrdersGrid={dataProductOrdersGrid}
                dataProductReturns={dataProductReturns}
                loading={loading}
                onFetchStockMovements={handleGetProductDetailStockMovementsFetch}
                onFetchProductReceivings={handleGetProductDetailProductReceivingsFetch}
                onFetchProductOrdersGrid={handleGetProductDetailProductOrdersGridFetch}
                onFetchProductReturns={handleGetProductDetailProductReturnsFetch}
                stockMovementsTableRef={stockMovementsTableRef}
                productReceivingsTableRef={productReceivingsTableRef}
                productOrdersTableRef={productOrdersTableRef}
                productReturnsTableRef={productReturnsTableRef}
                idProduct={idProduct || ''}
                receivingStatusCodes={receivingStatusCodes}
                receivingStatusCodesObject={receivingStatusCodesObject}
                permissionReceivingDetail={permissionReceivingDetail}
                permissionReturnDetail={permissionReturnDetail}
                returnStatusCodes={returnStatusCodes}
                returnStatusCodesObject={returnStatusCodesObject}
                permissionOrderDetail={permissionOrderDetail}
                orderStatusCodes={orderStatusCodes}
                orderStatusCodesObject={orderStatusCodesObject}
                shipperCodes={shipperCodes}
                serviceCodes={serviceCodes}
                paymentCodes={paymentCodes}
                orderMarketplacesCodes={orderMarketplacesCodes}
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                productInsetTypeCodesObject={productInsetTypeCodesObject}
                // countryOptionsCodesObject={countryOptionsCodesObject}
                boolNumberCodesObject={boolNumberCodesObject}
                scanImeiSnCodesObject={scanImeiSnCodesObject}
                activeNumberCodesObject={activeNumberCodesObject}
                expeditionMethodCodesObject={expeditionMethodCodesObject}
                currencyCodesObject={currencyCodesObject}
                productsListData={relationProductsList}
                stockMovementTypeCodes={stockMovementTypeCodes}
                insertByOrderTypeCodes={insertByOrderTypeCodes}
                insertByOrderTypeCodesObject={insertByOrderTypeCodesObject}
                skladonUser={skladonUser}
                onChangeTab={(value) => setActiveTab(value)}
                canDeactivateRemoveEans={permissionEanEdit || !editData.EAN_LOCKED_BY_DCIX}
            />

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={(): any => setConfirmParams(null)}
                    okLabel={confirmParams.okLabel}
                />
            )}
            {showProductStockTable && (
                <ProductStockTable
                    onClose={() => {
                        setShowProductStockTable(false);
                    }}
                    getProductDetailStockFetch={handleGetProductDetailStockFetch}
                    dataStock={dataStock}
                    loading={loading}
                    stockRation={stockRation}
                    customsWarehouseEnabled={!!customsWarehouseEnabled}
                />
            )}
        </>
    );
};

export default ProductDetailContainer;
